import { reportClientError } from "@/lib/sentry";
import React from "react";
import { AlertCircle, RotateCcw, Home, Unplug } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";

interface ErrorState {
  hasError: boolean;
  error: Error | null;
  retryCount: number;
}

/**
 * A failed dynamic import (chunk load) on a slow/flaky connection is almost
 * always a network problem, not an app bug. Detect it so we can show a
 * friendly "connection" message instead of the raw technical error.
 */
function isNetworkOrChunkError(error: Error | null): boolean {
  if (typeof navigator !== "undefined" && navigator.onLine === false) return true;
  const message = (error?.message || "").toLowerCase();
  return (
    message.includes("dynamically imported module") ||
    message.includes("loading chunk") ||
    message.includes("failed to fetch") ||
    message.includes("importing a module script failed") ||
    message.includes("networkerror") ||
    error?.name === "ChunkLoadError"
  );
}

interface InnerProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
  /** Changes whenever the route location changes; used to auto-reset the boundary. */
  resetKey: string;
  /** SPA navigation helpers injected from the functional wrapper. */
  onGoHome: () => void;
  onSoftReset: () => void;
}

class RouteErrorBoundaryInner extends React.Component<InnerProps, ErrorState> {
  state: ErrorState = { hasError: false, error: null, retryCount: 0 };

  static getDerivedStateFromError(error: Error): Partial<ErrorState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    reportClientError(error, "RouteErrorBoundary");
    console.error("[RouteErrorBoundary]", error, info.componentStack);
  }

  componentDidUpdate(prevProps: InnerProps) {
    // The user navigated to a different route (e.g. clicked a sidebar item).
    // React reuses this boundary instance across route changes, so without
    // this reset a page that crashed once would keep showing the error
    // screen on every subsequent route — leaving a full reload as the only
    // escape. Clearing the error here lets navigation itself recover.
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false, error: null, retryCount: 0 });
    }
  }

  handleRetry = () => {
    // First press: remount the route subtree in place (no reload) to recover
    // from a transient render error. If that still fails, fall back to a hard
    // reload as a last resort for genuinely broken chunks/state.
    if (this.state.retryCount >= 1) {
      this.props.onSoftReset();
      window.location.reload();
      return;
    }
    this.setState((prev) => ({
      hasError: false,
      error: null,
      retryCount: prev.retryCount + 1,
    }));
  };

  render() {
    if (this.state.hasError) {
      const isNetwork = isNetworkOrChunkError(this.state.error);
      return this.props.fallback || (
        <div className="flex flex-col items-center justify-center min-h-[calc(100dvh-5rem)] px-6 pb-24 pt-6 text-center">
          <div className={`w-16 h-16 rounded-3xl flex items-center justify-center mb-5 ${isNetwork ? "bg-amber-500/10 border border-amber-500/20" : "bg-red-500/10 border border-red-500/20"}`}>
            {isNetwork ? (
              <Unplug className="w-8 h-8 text-amber-400" />
            ) : (
              <AlertCircle className="w-8 h-8 text-red-400" />
            )}
          </div>
          <h2 className="text-xl font-bold text-[var(--text-primary)] mb-2">
            {isNetwork ? "No internet connection" : "This page hit a snag"}
          </h2>
          <p className="text-sm text-[var(--text-secondary)] mb-6 max-w-sm leading-relaxed">
            {isNetwork
              ? "Your connection looks slow or offline. Check your internet and try again. Your progress is safe."
              : `${this.state.error?.message || "An unexpected error occurred"}. Your progress elsewhere is safe.`}
          </p>
          <div className="flex gap-3">
            <button
              onClick={this.handleRetry}
              className="flex items-center gap-2 px-6 h-12 bg-[var(--accent-primary)] text-white rounded-2xl font-bold text-sm hover:brightness-110 transition-all active:scale-95"
            >
              <RotateCcw className="w-4 h-4" />
              {this.state.retryCount >= 1 ? "Reload Page" : "Try Again"}
            </button>
            <button
              onClick={this.props.onGoHome}
              className="flex items-center gap-2 px-6 h-12 bg-white/5 border border-white/10 text-[var(--text-secondary)] rounded-2xl font-bold text-sm hover:bg-white/10 hover:text-[var(--text-primary)] transition-all active:scale-95"
            >
              <Home className="w-4 h-4" />
              Go Home
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

/**
 * Functional wrapper that feeds the class boundary the current location (so it
 * can auto-reset on navigation) and SPA navigation callbacks (so recovery no
 * longer forces a full-page reload).
 */
export function RouteErrorBoundary({
  children,
  fallback,
}: {
  children: React.ReactNode;
  fallback?: React.ReactNode;
}) {
  const location = useLocation();
  const navigate = useNavigate();
  return (
    <RouteErrorBoundaryInner
      resetKey={location.pathname}
      fallback={fallback}
      onGoHome={() => navigate("/dashboard")}
      onSoftReset={() => {}}
    >
      {children}
    </RouteErrorBoundaryInner>
  );
}

export default RouteErrorBoundary;
